import React from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useNavigate } from "react-router-dom";
import ExpandButton from "@/components/ExpandButton";
import {
  BOOKED_REVENUE_CHART_COLOR,
  BOOKED_REVENUE_TOOLTIP_STYLE,
  BOOKED_REVENUE_TITLE,
  formatRevenueAxisLabel,
  formatRevenueTooltipValue,
} from "./booked-revenue-style";
import "./Widgets.scss";

function RevenueTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div style={BOOKED_REVENUE_TOOLTIP_STYLE.contentStyle}>
      <p className="mb-1 small text-muted">{label}</p>
      <p className="mb-0 fw-semibold">{formatRevenueTooltipValue(payload[0].value)}</p>
    </div>
  );
}

export function BookedRevenueWidget({
  value,
  change,
  trend,
  subtitle,
  miniChartData,
  expandedPath,
  expandedState,
}) {
  const navigate = useNavigate();

  return (
    <div
      className="bg-white px-4 py-3 rounded-3 shadow-sm d-flex flex-column dashboard-card"
      style={{ border: "1px solid #e0e0e0" }}
    >
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ marginBottom: "0.25rem" }}
      >
        <h6
          className="fw-semibold fs-5 mb-0"
          style={{
            color: "#2E2868",
            display: "flex",
            position: "relative",
            top: "-5.5px",
          }}
        >
          {BOOKED_REVENUE_TITLE}
        </h6>

        <div
          style={{
            display: "flex",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "relative",
              top: "-5px",
            }}
          >
            <ExpandButton onClick={() => navigate(expandedPath, { state: expandedState })} />
          </div>
        </div>
      </div>

      <div
        style={{
          height: "2px",
          backgroundColor: "#f3f4f6",
          marginTop: "0.1rem",
          marginBottom: "0.25rem",
          width: "calc(100% + 3rem)",
          marginLeft: "-1.5rem",
        }}
      />

      <div className="d-flex flex-column gap-2 pt-2 flex-grow-1 booked-revenue-content">
        <div className="d-flex align-items-baseline gap-3">
          <span className="kpi-value fw-bold">{value}</span>
          <span
            className={`d-inline-flex align-items-center gap-1 small fw-semibold ${
              trend === "up" ? "text-success" : trend === "down" ? "text-danger" : "text-secondary"
            }`}
          >
            <span aria-hidden="true">{trend === "up" ? "^" : trend === "down" ? "v" : "-"}</span>
            {change}
          </span>
        </div>
        <p className="text-muted small mb-1">{subtitle}</p>
        <div className="chart-box booked-revenue-chart-box">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={miniChartData} margin={{ top: 4, right: 4, bottom: 18, left: 6 }}>
              <defs>
                <linearGradient id="booked-revenue-widget-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BOOKED_REVENUE_CHART_COLOR} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={BOOKED_REVENUE_CHART_COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "#6c757d" }}
                minTickGap={18}
                tickLine={false}
                axisLine={{ stroke: "#cbd5e1", strokeWidth: 1 }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#6c757d" }}
                tickFormatter={formatRevenueAxisLabel}
                tickLine={false}
                axisLine={{ stroke: "#cbd5e1", strokeWidth: 1 }}
                width={40}
              />
              <Tooltip content={<RevenueTooltip />} cursor={{ stroke: "#cbd5e1", strokeDasharray: "4 4" }} />
              <Area
                type="monotone"
                dataKey="value"
                stroke={BOOKED_REVENUE_CHART_COLOR}
                strokeWidth={2}
                fill="url(#booked-revenue-widget-gradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default BookedRevenueWidget;
